import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, RefreshControl } from 'react-native';
import LoadingSpinner from './LoadingSpinner';
import { styles } from './playersCacheTabStyles';

/**
 * Admin Players Cache Tab
 * 
 * Features:
 * - View all cached players by sport/league
 * - Search players
 * - Refresh players cache
 * - View player details and stats
 * - (Future) Cache player props
 */
const PlayersCacheTab = () => {
  const [players, setPlayers] = useState([]);
  const [stats, setStats] = useState(null);
  const [propsStats, setPropsStats] = useState(null); // ⭐ Player props cache stats
  const [playersWithProps, setPlayersWithProps] = useState({}); // ⭐ Map of player name -> props count
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingProps, setRefreshingProps] = useState(false); // ⭐ Props refresh state
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({}); // Track which teams are expanded
  const [playerProps, setPlayerProps] = useState({}); // ⭐ Store fetched props per player
  const [loadingProps, setLoadingProps] = useState({}); // ⭐ Track which players are loading props
  
  // 🏥 Injury Report state
  const [injuryData, setInjuryData] = useState(null);
  const [injuryLoading, setInjuryLoading] = useState(true);
  const [injuryRefreshing, setInjuryRefreshing] = useState(false);
  const [injuryLeagueFilter, setInjuryLeagueFilter] = useState('all');
  const [injuryStatusFilter, setInjuryStatusFilter] = useState('all');
  const [expandedInjuryTeams, setExpandedInjuryTeams] = useState({});
  const [injuryCollapsed, setInjuryCollapsed] = useState(false);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
    
  // Always use production for props (too slow for emulator)
  const propsUrl = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

  const leagues = [
    { key: 'all', label: 'All', icon: '🌐' },
    { key: 'nba', label: 'NBA', icon: '🏀' },
    { key: 'nfl', label: 'NFL', icon: '🏈' },
    { key: 'nhl', label: 'NHL', icon: '🏒' },
    { key: 'mlb', label: 'MLB', icon: '⚾' },
    { key: 'ncaab', label: 'NCAAB', icon: '🎓🏀' },
    { key: 'ncaaf', label: 'NCAAF', icon: '🎓🏈' },
    { key: 'wnba', label: 'WNBA', icon: '🏀' },
  ];

  // Fetch players cache stats
  const fetchPlayersStats = async () => {
    try {
      const response = await fetch(`${functionsUrl}/getPlayersCacheStats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data);
        }
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Stats error:', err);
    }
  };

  // ⭐ Fetch player PROPS cache stats
  const fetchPropsStats = async () => {
    try {
      const response = await fetch(`${propsUrl}/getPlayerPropsCacheStats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPropsStats(data);
        }
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Props stats error:', err);
    }
  };

  // ⭐ Fetch list of all players with props (for counts)
  const fetchPlayersWithProps = async () => {
    try {
      const response = await fetch(`${propsUrl}/listPlayerProps?limit=1000`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.players) {
          // Build a map of player name (lowercase) -> props info
          const propsMap = {};
          data.players.forEach(p => {
            const key = p.player_name?.toLowerCase();
            if (key) {
              propsMap[key] = {
                props_count: p.props_count || 0,
                markets_available: p.markets_available || [],
                team: p.team,
                league: p.league
              };
            }
          });
          setPlayersWithProps(propsMap);
          console.log('[PlayersCacheTab] Loaded props for', Object.keys(propsMap).length, 'players');
        }
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Error fetching players with props:', err);
    }
  };

  // ⭐ Fetch props for a specific player
  const fetchPlayerProps = async (playerName, league) => {
    const key = `${playerName}_${league}`;
    if (playerProps[key]) return; // Already fetched
    
    setLoadingProps(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await fetch(
        `${propsUrl}/getPlayerProps?player=${encodeURIComponent(playerName)}&league=${encodeURIComponent(league)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success || data.props) {
          setPlayerProps(prev => ({ ...prev, [key]: data }));
        }
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Error fetching props:', err);
    } finally {
      setLoadingProps(prev => ({ ...prev, [key]: false }));
    }
  };

  // ⭐ Refresh player props cache
  const triggerPropsRefresh = async () => {
    try {
      setRefreshingProps(true);
      
      // Use production URL for props refresh (takes too long for local emulator)
      const propsRefreshUrl = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net/refreshPlayerPropsCache';
      
      const response = await fetch(propsRefreshUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Player Props Cache refreshed!\n\nPlayers: ${data.total_players}\nProps: ${data.total_props?.toLocaleString()}\nDuration: ${(data.duration_ms / 1000).toFixed(1)}s`);
        await fetchPropsStats();
        await fetchPlayersWithProps(); // Refresh the props list
        setPlayerProps({}); // Clear cached props to refetch
      } else {
        throw new Error(data.error || 'Failed to refresh');
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Props refresh error:', err);
      alert(`❌ Error: ${err.message}\n\nTry refreshing from production site.`);
    } finally {
      setRefreshingProps(false);
    }
  };

  // 🏥 Fetch injury report from cache
  const fetchInjuryReport = async (league = 'all', status = 'all') => {
    try {
      setInjuryLoading(true);
      let url = `${functionsUrl}/getInjuryReport?league=${encodeURIComponent(league)}`;
      if (status && status !== 'all') {
        url += `&status=${encodeURIComponent(status)}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setInjuryData(data);
        }
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Injury report error:', err);
    } finally {
      setInjuryLoading(false);
    }
  };

  // 🏥 Trigger injury-only refresh
  const triggerInjuryRefresh = async () => {
    try {
      setInjuryRefreshing(true);
      
      const response = await fetch(`${functionsUrl}/refreshInjuries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Injury data refreshed!\n\nUpdated: ${data.total_updated} players\nInjured: ${data.total_injured}\nDuration: ${(data.duration_ms / 1000).toFixed(1)}s`);
        await fetchInjuryReport(injuryLeagueFilter, injuryStatusFilter);
      } else {
        throw new Error(data.error || 'Failed to refresh');
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Injury refresh error:', err);
      alert(`Error refreshing injuries: ${err.message}`);
    } finally {
      setInjuryRefreshing(false);
    }
  };

  // 🏥 Injury status badge config
  const getInjuryStatusConfig = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'out') return { label: 'OUT', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' };
    if (s === 'doubtful') return { label: 'DOUBTFUL', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' };
    if (s === 'questionable') return { label: 'QUESTIONABLE', color: '#EAB308', bg: 'rgba(234, 179, 8, 0.15)' };
    if (s === 'day-to-day' || s === 'day_to_day') return { label: 'DAY-TO-DAY', color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.15)' };
    if (s === 'probable') return { label: 'PROBABLE', color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' };
    if (s === 'suspended') return { label: 'SUSPENDED', color: '#A855F7', bg: 'rgba(168, 85, 247, 0.15)' };
    return { label: s.toUpperCase() || 'UNKNOWN', color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.15)' };
  };

  // Fetch players from cache
  const fetchPlayers = async (league = 'all', search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch more players - use higher limit for full roster view
      let url = `${functionsUrl}/getPlayersCache?limit=2000`;
      if (league && league !== 'all') {
        url += `&league=${encodeURIComponent(league)}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setPlayers(data.players || []);
      } else {
        throw new Error(data.error || 'Failed to fetch players');
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Error:', err);
      setError(err.message);
      setPlayers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Trigger players cache refresh
  const triggerRefresh = async () => {
    try {
      setRefreshing(true);
      
      const response = await fetch(`${functionsUrl}/refreshPlayersCache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Players cache refreshed!\n\nTotal: ${data.total_players} players\nEntries: ${data.total_entries}\nDuration: ${(data.duration_ms / 1000).toFixed(1)}s`);
        // Refresh the list
        await fetchPlayers(selectedLeague, searchQuery);
        await fetchPlayersStats();
      } else {
        throw new Error(data.error || 'Failed to refresh');
      }
    } catch (err) {
      console.error('[PlayersCacheTab] Refresh error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchPlayers();
    fetchPlayersStats();
    fetchPropsStats(); // ⭐ Also fetch props stats
    fetchPlayersWithProps(); // ⭐ Fetch players with props for counts
    fetchInjuryReport(); // 🏥 Fetch injury report
  }, []);

  // Handle league filter change
  const handleLeagueChange = (league) => {
    setSelectedLeague(league);
    fetchPlayers(league, searchQuery);
  };

  // Handle search
  const handleSearch = () => {
    fetchPlayers(selectedLeague, searchQuery);
  };

  // Group players by team for better display
  const groupedPlayers = players.reduce((acc, player) => {
    const team = player.team || 'Unknown Team';
    if (!acc[team]) {
      acc[team] = [];
    }
    acc[team].push(player);
    return acc;
  }, {});

  const teamNames = Object.keys(groupedPlayers).sort();
  
  // ⭐ Calculate props count per team
  const getTeamPropsCount = (teamPlayers) => {
    let total = 0;
    let playersWithPropsCount = 0;
    teamPlayers.forEach(player => {
      const key = player.name?.toLowerCase();
      if (key && playersWithProps[key]) {
        total += playersWithProps[key].props_count || 0;
        playersWithPropsCount++;
      }
    });
    return { total, playersWithPropsCount };
  };
  
  // ⭐ Get props count for a single player
  const getPlayerPropsCount = (playerName) => {
    const key = playerName?.toLowerCase();
    return playersWithProps[key]?.props_count || 0;
  };

  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={() => {
            setRefreshing(true);
            fetchPlayers(selectedLeague, searchQuery);
            fetchPlayersStats();
            fetchPropsStats(); // ⭐ Also refresh props stats
            fetchInjuryReport(injuryLeagueFilter, injuryStatusFilter); // 🏥 Also refresh injury report
          }}
          tintColor="#10B981"
        />
      }
    >
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* TOP ROW: 3 stat cards side by side */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <View style={styles.topCardsRow}>
        {/* 🏥 Injury Report Card */}
        <View style={[styles.topCard, { borderLeftWidth: 3, borderLeftColor: '#EF4444' }]}>
          <TouchableOpacity 
            style={styles.headerRow}
            onPress={() => setInjuryCollapsed(!injuryCollapsed)}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap' }}>
              <Text style={{ fontSize: 12, color: '#EF4444' }}>{injuryCollapsed ? '▶' : '▼'}</Text>
              <Text style={styles.cardTitle}>🏥 Injury Report</Text>
              {injuryData && (
                <View style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12 }}>
                  <Text style={{ color: '#FCA5A5', fontSize: 12, fontWeight: '700' }}>
                    {injuryData.total_injured} injured
                  </Text>
                </View>
              )}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {injuryData?.last_injury_refresh && (
                <Text style={{ color: '#64748B', fontSize: 11 }}>
                  Updated {new Date(injuryData.last_injury_refresh).toLocaleString()}
                </Text>
              )}
              <TouchableOpacity 
                style={[styles.refreshButton, { backgroundColor: '#EF4444' }, injuryRefreshing && styles.refreshButtonDisabled]}
                onPress={(e) => { e.stopPropagation(); triggerInjuryRefresh(); }}
                disabled={injuryRefreshing}
              >
                {injuryRefreshing ? (
                  <LoadingSpinner size={16} color="#FFFFFF" />
                ) : (
                  <Text style={styles.refreshButtonText}>Refresh Injuries</Text>
                )}
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        
        {!injuryCollapsed && (
          <ScrollView style={styles.injuryScrollContainer} nestedScrollEnabled>
            {injuryLoading ? (
              <View style={styles.loadingContainer}>
                <LoadingSpinner size={24} color="#EF4444" />
                <Text style={styles.loadingText}>Loading injury report...</Text>
              </View>
            ) : injuryData ? (
              <>
                {/* Status Breakdown Summary */}
                {injuryData.status_breakdown && Object.keys(injuryData.status_breakdown).length > 0 && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {Object.entries(injuryData.status_breakdown)
                      .sort((a, b) => b[1] - a[1])
                      .map(([status, count]) => {
                        const config = getInjuryStatusConfig(status);
                        const isActive = injuryStatusFilter === status;
                        return (
                          <TouchableOpacity 
                            key={status}
                            style={{
                              backgroundColor: isActive ? config.color : config.bg,
                              paddingHorizontal: 12,
                              paddingVertical: 6,
                              borderRadius: 16,
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 6,
                              borderWidth: 1,
                              borderColor: isActive ? config.color : 'transparent'
                            }}
                            onPress={() => {
                              const newFilter = isActive ? 'all' : status;
                              setInjuryStatusFilter(newFilter);
                              fetchInjuryReport(injuryLeagueFilter, newFilter);
                            }}
                          >
                            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: isActive ? '#FFFFFF' : config.color }} />
                            <Text style={{ color: isActive ? '#FFFFFF' : config.color, fontSize: 12, fontWeight: '700' }}>
                              {config.label}
                            </Text>
                            <Text style={{ color: isActive ? 'rgba(255,255,255,0.8)' : config.color, fontSize: 12, fontWeight: '600' }}>
                              {count}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    {injuryStatusFilter !== 'all' && (
                      <TouchableOpacity 
                        style={{ backgroundColor: '#475569', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 }}
                        onPress={() => { setInjuryStatusFilter('all'); fetchInjuryReport(injuryLeagueFilter, 'all'); }}
                      >
                        <Text style={{ color: '#F8FAFC', fontSize: 12, fontWeight: '600' }}>Show All</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
                
                {/* League Filter Pills for Injuries */}
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
                  {[{ key: 'all', label: 'All Leagues' }, ...leagues.filter(l => l.key !== 'all')].map(league => {
                    const isActive = injuryLeagueFilter === league.key;
                    const count = league.key === 'all' 
                      ? injuryData.total_injured 
                      : (injuryData.league_breakdown?.[league.key] || 0);
                    if (league.key !== 'all' && count === 0) return null;
                    return (
                      <TouchableOpacity
                        key={league.key}
                        style={[
                          styles.leaguePill,
                          isActive && { backgroundColor: '#EF4444' }
                        ]}
                        onPress={() => {
                          setInjuryLeagueFilter(league.key);
                          fetchInjuryReport(league.key, injuryStatusFilter);
                        }}
                      >
                        <Text style={[
                          styles.leaguePillText,
                          isActive && styles.leaguePillTextActive
                        ]}>
                          {league.key === 'all' ? '🌐' : league.icon} {league.label} ({count})
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
                
                {/* Grouped Injury List */}
                {injuryData.grouped && Object.keys(injuryData.grouped).length > 0 ? (
                  <View>
                    {Object.entries(injuryData.grouped).sort().map(([leagueKey, teams]) => (
                      <View key={leagueKey} style={{ marginBottom: 12 }}>
                        <Text style={{ color: '#F8FAFC', fontSize: 14, fontWeight: '700', marginBottom: 8, textTransform: 'uppercase' }}>
                          {leagueKey}
                        </Text>
                        {Object.entries(teams).sort().map(([teamName, teamPlayers]) => {
                          const isExpanded = expandedInjuryTeams[`${leagueKey}_${teamName}`];
                          return (
                            <View key={teamName} style={{ marginBottom: 6 }}>
                              <TouchableOpacity 
                                style={{ 
                                  flexDirection: 'row', 
                                  alignItems: 'center', 
                                  backgroundColor: '#334155', 
                                  padding: 10, 
                                  borderRadius: 8, 
                                  gap: 8 
                                }}
                                onPress={() => setExpandedInjuryTeams(prev => ({
                                  ...prev,
                                  [`${leagueKey}_${teamName}`]: !prev[`${leagueKey}_${teamName}`]
                                }))}
                              >
                                <Text style={{ fontSize: 11, color: '#EF4444', width: 14 }}>
                                  {isExpanded ? '▼' : '▶'}
                                </Text>
                                <Text style={{ color: '#F8FAFC', fontSize: 13, fontWeight: '600', flex: 1 }}>
                                  {teamName}
                                </Text>
                                <View style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 }}>
                                  <Text style={{ color: '#FCA5A5', fontSize: 11, fontWeight: '700' }}>
                                    {teamPlayers.length}
                                  </Text>
                                </View>
                              </TouchableOpacity>
                              
                              {isExpanded && teamPlayers.map((player, idx) => {
                                const statusConfig = getInjuryStatusConfig(player.injury_status);
                                return (
                                  <View 
                                    key={player.player_id || idx} 
                                    style={{ 
                                      backgroundColor: '#1E293B', 
                                      marginLeft: 14, 
                                      marginTop: 4, 
                                      padding: 10, 
                                      borderRadius: 6, 
                                      borderLeftWidth: 3, 
                                      borderLeftColor: statusConfig.color 
                                    }}
                                  >
                                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <View style={{ flex: 1 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                          <Text style={{ color: '#F8FAFC', fontSize: 14, fontWeight: '600' }}>
                                            {player.name}
                                          </Text>
                                          {player.position && (
                                            <Text style={{ color: '#3B82F6', fontSize: 11, fontWeight: '500' }}>
                                              {player.position}
                                            </Text>
                                          )}
                                          {player.jersey_number && (
                                            <Text style={{ color: '#64748B', fontSize: 11 }}>
                                              #{player.jersey_number}
                                            </Text>
                                          )}
                                        </View>
                                        
                                        {/* Injury Details */}
                                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                                          {player.injury_details?.injury_type && (
                                            <Text style={{ color: '#94A3B8', fontSize: 11 }}>
                                              {player.injury_details.injury_type}
                                            </Text>
                                          )}
                                          {player.injury_details?.body_part && (
                                            <Text style={{ color: '#94A3B8', fontSize: 11 }}>
                                              ({player.injury_details.body_part})
                                            </Text>
                                          )}
                                          {player.injury_details?.description && (
                                            <Text style={{ color: '#64748B', fontSize: 11 }}>
                                              - {player.injury_details.description}
                                            </Text>
                                          )}
                                          {player.injury_details?.designation && !player.injury_details?.injury_type && (
                                            <Text style={{ color: '#94A3B8', fontSize: 11 }}>
                                              {player.injury_details.designation}
                                            </Text>
                                          )}
                                        </View>
                                      </View>
                                      
                                      {/* Status Badge */}
                                      <View style={{ 
                                        backgroundColor: statusConfig.bg, 
                                        paddingHorizontal: 10, 
                                        paddingVertical: 4, 
                                        borderRadius: 6,
                                        borderWidth: 1,
                                        borderColor: statusConfig.color + '40'
                                      }}>
                                        <Text style={{ color: statusConfig.color, fontSize: 11, fontWeight: '700' }}>
                                          {statusConfig.label}
                                        </Text>
                                      </View>
                                    </View>
                                  </View>
                                );
                              })}
                            </View>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No injured players found</Text>
                    <Text style={styles.emptySubtext}>All players appear to be active</Text>
                  </View>
                )}
              </>
            ) : (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>No injury data available</Text>
                <Text style={styles.emptySubtext}>Tap "Refresh Injuries" to fetch the latest data</Text>
              </View>
            )}
          </ScrollView>
        )}
        </View>

        {/* Header Stats Card */}
        <View style={[styles.topCard]}>
        <View style={styles.headerRow}>
          <Text style={styles.cardTitle}>👥 Players Cache</Text>
          <TouchableOpacity 
            style={[styles.refreshButton, refreshing && styles.refreshButtonDisabled]}
            onPress={triggerRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <LoadingSpinner size={16} color="#FFFFFF" />
            ) : (
              <Text style={styles.refreshButtonText}>🔄 Refresh Cache</Text>
            )}
          </TouchableOpacity>
        </View>
        
        {stats && (
          <View style={styles.statsGrid}>
            <View style={styles.statBox}>
              <Text style={styles.statNumber}>{stats.total_players?.toLocaleString() || 0}</Text>
              <Text style={styles.statLabel}>Total Players</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statNumber}>{stats.total_entries?.toLocaleString() || 0}</Text>
              <Text style={styles.statLabel}>Search Entries</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statNumber}>{stats.leagues_count || 0}</Text>
              <Text style={styles.statLabel}>Leagues</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statNumber}>{stats.last_refresh ? new Date(stats.last_refresh).toLocaleDateString() : 'Never'}</Text>
              <Text style={styles.statLabel}>Last Refresh</Text>
            </View>
          </View>
        )}
        
        {/* League breakdown */}
        {stats?.by_league && (
          <View style={styles.leagueBreakdown}>
            <Text style={styles.breakdownTitle}>By League:</Text>
            <View style={styles.breakdownGrid}>
              {Object.entries(stats.by_league).map(([league, count]) => (
                <View key={league} style={styles.breakdownItem}>
                  <Text style={styles.breakdownLeague}>{league.toUpperCase()}</Text>
                  <Text style={styles.breakdownCount}>{count}</Text>
                </View>
              ))}
            </View>
          </View>
        )}
        </View>

        {/* ⭐ Player Props Cache Stats Card */}
        <View style={[styles.topCard, { borderLeftWidth: 3, borderLeftColor: '#8B5CF6' }]}>
        <View style={styles.headerRow}>
          <Text style={styles.cardTitle}>🎯 Player Props Cache</Text>
          <TouchableOpacity 
            style={[styles.refreshButton, { backgroundColor: '#8B5CF6' }, refreshingProps && styles.refreshButtonDisabled]}
            onPress={triggerPropsRefresh}
            disabled={refreshingProps}
          >
            {refreshingProps ? (
              <LoadingSpinner size={16} color="#FFFFFF" />
            ) : (
              <Text style={styles.refreshButtonText}>🔄 Refresh Props</Text>
            )}
          </TouchableOpacity>
        </View>
        
        {propsStats ? (
          <>
            <View style={styles.statsGrid}>
              <View style={styles.statBox}>
                <Text style={[styles.statNumber, { color: '#8B5CF6' }]}>{propsStats.total_players?.toLocaleString() || 0}</Text>
                <Text style={styles.statLabel}>Players w/ Props</Text>
              </View>
              <View style={styles.statBox}>
                <Text style={[styles.statNumber, { color: '#F59E0B' }]}>{propsStats.total_documents?.toLocaleString() || 0}</Text>
                <Text style={styles.statLabel}>Cached Docs</Text>
              </View>
              <View style={styles.statBox}>
                <Text style={[styles.statNumber, { color: '#10B981' }]}>{propsStats.last_refresh_age_minutes != null ? `${propsStats.last_refresh_age_minutes}m` : 'N/A'}</Text>
                <Text style={styles.statLabel}>Last Refresh</Text>
              </View>
              <View style={styles.statBox}>
                <Text style={[styles.statNumber, { color: '#3B82F6' }]}>{propsStats.next_refresh_in || 'Unknown'}</Text>
                <Text style={styles.statLabel}>Next Refresh</Text>
              </View>
            </View>
            
            {/* Props by league */}
            {propsStats.by_league && Object.keys(propsStats.by_league).length > 0 && (
              <View style={styles.leagueBreakdown}>
                <Text style={styles.breakdownTitle}>Props by League:</Text>
                <View style={styles.breakdownGrid}>
                  {Object.entries(propsStats.by_league).map(([league, count]) => (
                    <View key={league} style={[styles.breakdownItem, { backgroundColor: '#4C1D95' }]}>
                      <Text style={styles.breakdownLeague}>{league.toUpperCase()}</Text>
                      <Text style={[styles.breakdownCount, { color: '#C4B5FD' }]}>{count} players</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
            
            {/* Last stats from refresh */}
            {propsStats.last_stats && Object.keys(propsStats.last_stats).length > 0 && (
              <View style={{ marginTop: 12, backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: 8, padding: 12 }}>
                <Text style={{ color: '#C4B5FD', fontSize: 12, fontWeight: '600', marginBottom: 8 }}>📊 Last Refresh Stats:</Text>
                {Object.entries(propsStats.last_stats).map(([league, data]) => (
                  <View key={league} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ color: '#E0E7FF', fontSize: 12 }}>{league.toUpperCase()}</Text>
                    <Text style={{ color: '#A5B4FC', fontSize: 12 }}>
                      {data.fixtures || 0} fixtures • {data.players || 0} players • {(data.props || 0).toLocaleString()} props
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </>
        ) : (
          <View style={{ alignItems: 'center', padding: 20 }}>
            <LoadingSpinner size={24} color="#8B5CF6" />
            <Text style={{ color: '#94A3B8', marginTop: 8, fontSize: 13 }}>Loading props cache stats...</Text>
          </View>
        )}
        </View>
      </View>
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* END TOP ROW */}
      {/* ═══════════════════════════════════════════════════════════════════ */}

      {/* Search & Filter */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>🔍 Search Players</Text>
        
        <View style={styles.searchRow}>
          <TextInput
            style={styles.searchInput}
            placeholder="Search by name..."
            placeholderTextColor="#6B7280"
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={handleSearch}
          />
          <TouchableOpacity style={styles.searchButton} onPress={handleSearch}>
            <Text style={styles.searchButtonText}>Search</Text>
          </TouchableOpacity>
        </View>
        
        {/* League Filter Pills */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.leaguePills}>
          {leagues.map(league => (
            <TouchableOpacity
              key={league.key}
              style={[
                styles.leaguePill,
                selectedLeague === league.key && styles.leaguePillActive
              ]}
              onPress={() => handleLeagueChange(league.key)}
            >
              <Text style={[
                styles.leaguePillText,
                selectedLeague === league.key && styles.leaguePillTextActive
              ]}>
                {league.icon} {league.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Players List */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          📋 Players ({players.length})
        </Text>
        
        {loading ? (
          <View style={styles.loadingContainer}>
            <LoadingSpinner size={32} color="#10B981" />
            <Text style={styles.loadingText}>Loading players...</Text>
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>❌ {error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={() => fetchPlayers(selectedLeague, searchQuery)}>
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : players.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No players found</Text>
            <Text style={styles.emptySubtext}>Try a different search or refresh the cache</Text>
          </View>
        ) : (
          <View style={styles.playersList}>
            {/* Summary & Controls */}
            <View style={styles.listControls}>
              <Text style={styles.teamsSummary}>
                {teamNames.length} teams • {players.length} players
              </Text>
              <View style={styles.expandControls}>
                <TouchableOpacity 
                  style={styles.expandAllBtn}
                  onPress={() => {
                    const allExpanded = {};
                    teamNames.forEach(t => allExpanded[t] = true);
                    setExpandedTeams(allExpanded);
                  }}
                >
                  <Text style={styles.expandAllBtnText}>Expand All</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.expandAllBtn}
                  onPress={() => setExpandedTeams({})}
                >
                  <Text style={styles.expandAllBtnText}>Collapse All</Text>
                </TouchableOpacity>
              </View>
            </View>
            
            {teamNames.map(teamName => {
              const isTeamExpanded = expandedTeams[teamName];
              const teamPlayers = groupedPlayers[teamName];
              const teamPropsInfo = getTeamPropsCount(teamPlayers);
              
              return (
                <View key={teamName} style={styles.teamGroup}>
                  {/* Collapsible Team Header */}
                  <TouchableOpacity 
                    style={styles.teamHeaderRow}
                    onPress={() => setExpandedTeams(prev => ({ ...prev, [teamName]: !prev[teamName] }))}
                  >
                    <Text style={styles.teamHeaderIcon}>{isTeamExpanded ? '▼' : '▶'}</Text>
                    <Text style={styles.teamHeader}>{teamName}</Text>
                    <Text style={styles.teamCount}>({teamPlayers.length})</Text>
                    {/* ⭐ Show props count for team */}
                    {teamPropsInfo.total > 0 && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 8, backgroundColor: '#4C1D95', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 }}>
                        <Text style={{ color: '#C4B5FD', fontSize: 11, fontWeight: '600' }}>
                          🎯 {teamPropsInfo.total.toLocaleString()} props
                        </Text>
                        <Text style={{ color: '#A78BFA', fontSize: 10, marginLeft: 4 }}>
                          ({teamPropsInfo.playersWithPropsCount} players)
                        </Text>
                      </View>
                    )}
                  </TouchableOpacity>
                  
                  {/* Player List (only shown when expanded) */}
                  {isTeamExpanded && teamPlayers.map((player, idx) => {
                    const playerPropsCount = getPlayerPropsCount(player.name);
                    const hasProps = playerPropsCount > 0;
                    
                    return (
                    <TouchableOpacity
                      key={`${player.player_id || idx}`}
                      style={[styles.playerRow, hasProps && { borderLeftWidth: 3, borderLeftColor: '#8B5CF6' }]}
                      onPress={() => setExpandedPlayer(expandedPlayer === player.player_id ? null : player.player_id)}
                    >
                      <View style={styles.playerInfo}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={styles.playerName}>{player.name}</Text>
                          {/* ⭐ Show props count badge */}
                          {hasProps && (
                            <View style={{ backgroundColor: '#8B5CF6', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 }}>
                              <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: '700' }}>
                                {playerPropsCount} props
                              </Text>
                            </View>
                          )}
                        </View>
                        <View style={styles.playerMeta}>
                          {player.position && (
                            <Text style={styles.playerPosition}>{player.position}</Text>
                          )}
                          {player.jersey_number && (
                            <Text style={styles.playerJersey}>#{player.jersey_number}</Text>
                          )}
                          <Text style={styles.playerLeague}>{player.league?.toUpperCase()}</Text>
                        </View>
                      </View>
                      <Text style={styles.expandIcon}>
                        {expandedPlayer === player.player_id ? '▼' : '▶'}
                      </Text>
                      
                      {/* Expanded Details */}
                      {expandedPlayer === player.player_id && (
                        <View style={styles.playerDetails}>
                          <Text style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Player ID: </Text>
                            <Text style={styles.detailValue}>{player.player_id || 'N/A'}</Text>
                          </Text>
                          <Text style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Sport: </Text>
                            <Text style={styles.detailValue}>{player.sport}</Text>
                          </Text>
                          <Text style={styles.detailRow}>
                            <Text style={styles.detailLabel}>League: </Text>
                            <Text style={styles.detailValue}>{player.league}</Text>
                          </Text>
                          <Text style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Updated: </Text>
                            <Text style={styles.detailValue}>
                              {player.updated_at ? new Date(player.updated_at).toLocaleString() : 'N/A'}
                            </Text>
                          </Text>
                          
                          {/* ⭐ Player Props Section */}
                          {(() => {
                            const propsKey = `${player.name}_${player.league}`;
                            const props = playerProps[propsKey];
                            const isLoading = loadingProps[propsKey];
                            
                            return (
                              <View style={{ marginTop: 12 }}>
                                {!props && !isLoading && (
                                  <TouchableOpacity 
                                    style={[styles.propsButton, { backgroundColor: '#8B5CF6' }]}
                                    onPress={() => fetchPlayerProps(player.name, player.league)}
                                  >
                                    <Text style={[styles.propsButtonText, { color: '#FFFFFF' }]}>📊 Load Props</Text>
                                  </TouchableOpacity>
                                )}
                                
                                {isLoading && (
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12 }}>
                                    <LoadingSpinner size={16} color="#8B5CF6" />
                                    <Text style={{ color: '#94A3B8', fontSize: 13 }}>Loading props...</Text>
                                  </View>
                                )}
                                
                                {props && props.props && (
                                  <View style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: 8, padding: 12, marginTop: 8 }}>
                                    <Text style={{ color: '#C4B5FD', fontSize: 13, fontWeight: '700', marginBottom: 8 }}>
                                      🎯 Available Props ({Object.keys(props.props).length} markets)
                                    </Text>
                                    {Object.entries(props.props).slice(0, 6).map(([market, lines]) => (
                                      <View key={market} style={{ marginBottom: 8 }}>
                                        <Text style={{ color: '#E0E7FF', fontSize: 12, fontWeight: '600', marginBottom: 4 }}>
                                          {market.replace(/_/g, ' ').replace(/player /i, '').toUpperCase()} ({lines.length} lines)
                                        </Text>
                                        {lines.slice(0, 2).map((line, i) => (
                                          <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingLeft: 8, marginBottom: 2 }}>
                                            <Text style={{ color: '#A5B4FC', fontSize: 11 }}>
                                              {line.is_over ? '⬆️ Over' : '⬇️ Under'} {line.line}
                                            </Text>
                                            <Text style={{ color: line.price > 0 ? '#10B981' : '#F59E0B', fontSize: 11, fontWeight: '600' }}>
                                              {line.price > 0 ? '+' : ''}{line.price} ({line.sportsbook})
                                            </Text>
                                          </View>
                                        ))}
                                        {lines.length > 2 && (
                                          <Text style={{ color: '#6B7280', fontSize: 10, paddingLeft: 8 }}>
                                            +{lines.length - 2} more lines...
                                          </Text>
                                        )}
                                      </View>
                                    ))}
                                    {Object.keys(props.props).length > 6 && (
                                      <Text style={{ color: '#6B7280', fontSize: 11, marginTop: 4, textAlign: 'center' }}>
                                        +{Object.keys(props.props).length - 6} more markets...
                                      </Text>
                                    )}
                                  </View>
                                )}
                                
                                {props && !props.props && (
                                  <View style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: 8, padding: 12, marginTop: 8 }}>
                                    <Text style={{ color: '#FCA5A5', fontSize: 12 }}>
                                      ❌ No props found for this player
                                    </Text>
                                  </View>
                                )}
                              </View>
                            );
                          })()}
                        </View>
                      )}
                    </TouchableOpacity>
                    );
                  })}
                </View>
              );
            })}
          </View>
        )}
      </View>
      
      {/* Spacer */}
      <View style={{ height: 40 }} />
    </ScrollView>
  );
};


export default PlayersCacheTab;
